import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { cloneDeep, findLast } from 'lodash';
import {
  Subject,
  Observable,
  catchError,
  forkJoin,
  of,
  map,
  takeUntil,
} from 'rxjs';
import { AuthService } from 'src/app/services/common/auth.service';
import { SnackbarService } from 'src/app/services/common/snackbar.service';
import { HealthAppService } from 'src/app/services/explore-health/health-app.service';
import {
  AgentStatusUpdate,
  AgentWorking,
  AttachedChatFile,
  CanvasEvents,
  CanvasStringConstants,
  ChatExpansion,
  ChatMessageType,
  ChatTypes,
  ConnectionReasons,
  ConversationSettings,
  ConverstationStatus,
  Session,
  SessionViewType,
  ShareAccessLevel,
  SharedUser,
  SockeConnectionType,
  SocketConnectionStage,
  SocketEvent,
  SocketMessage,
  SocketPayload,
  SocketResponseMessageType,
} from './canvas.model';
import { CanvasChatFeedbackDialogComponent } from './canvas-chat/canvas-chat-feedback-dialog/canvas-chat-feedback-dialog.component';
import { AddChatDocumentsDialogComponent } from './add-chat-documents-dialog/add-chat-documents-dialog.component';
import { AlertpopupComponent } from 'src/app/components/alertpopup/alertpopup.component';
import TurndownService from 'turndown';
import { Renderer, marked } from 'marked';
import { CanvasApiHandlerService } from './canvas-api-handler.service';
import { ChatSocketService } from './canvas-socket.service';

@Injectable()
export class CanvasService implements OnDestroy {
  private destroyNotifier$!: Subject<void>;

  private readonly turndownService = new TurndownService({
    headingStyle: 'atx',
  });

  public currentSessionId!: string | undefined;
  public currentUserEmail!: string;
  public currentSessionItem!: Session;
  public sessionViewType = SessionViewType.MY_SESSIONS;
  public agentStatusUpdates!: AgentStatusUpdate;
  public outlineDocument: any;
  public sessions: Session[] = [];
  public sourcesList: any = [];
  public currentChatMessages: any[] = [];
  public chatFilesList: any[] = [];
  public bookmarkIndex: number = 0;
  public bookmarkedResponses: any[] = [];
  public agentTyping: AgentWorking = {
    conversation_id: '',
    session_id: '',
    isAgentWorking: false,
    isAgentRegenerating: false,
  };
  public isEmbededChat: boolean = false;
  public isLoadingSessions: boolean = false;
  public isLoadingDocument: boolean = false;

  public chatWindowScreenSize: boolean = ChatExpansion.FULL_SCREEN_EXPANDED;
  public showSessionsPanel: boolean = ChatExpansion.SMALL_SCREEN_LEFT;
  public showCitationsPanel: boolean = ChatExpansion.SMALL_SCREEN_LEFT;
  private _conversationSettings!: ConversationSettings;
  private _appSettings: any;
  set conversationSettings(settings: ConversationSettings) {
    this._conversationSettings = settings;
  }
  set appSettings(settings: any) {
    this._appSettings = settings;
  }

  get appSettings(): any {
    this._appSettings ??= {};
    return this._appSettings;
  }

  get conversationSettings(): ConversationSettings {
    this._conversationSettings ??= {
      conversation_warning_limit: 0,
      conversation_max_limit: 0,
      session_share_max_limit: 0,
    };
    return this._conversationSettings;
  }

  get isContentEditorOpened(): boolean {
    return this.chatWindowScreenSize === ChatExpansion.SMALL_SCREEN_LEFT;
  }

  get isSessionReadOnly(): boolean {
    return this.currentSessionItem?.permission == ShareAccessLevel.READ_ONLY;
  }

  public clearCitations() {
    this.showCitationsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.sourcesList = null;
  }

  get isSidePanelOpened(): boolean {
    return (
      this.showSessionsPanel === ChatExpansion.FULL_SCREEN_EXPANDED ||
      this.showCitationsPanel === ChatExpansion.FULL_SCREEN_EXPANDED
    );
  }

  private readonly _canvasEvents$ = new Subject<{
    type: CanvasEvents;
    payload?: any;
  }>();
  public canvasEvents$: Observable<{ type: CanvasEvents; payload?: any }> =
    this._canvasEvents$.asObservable();

  private readonly _onBookmarkNavigationSubject$ = new Subject<any>();
  public onBookmarkNavigationSubject$: Observable<any> =
    this._onBookmarkNavigationSubject$.asObservable();

  private readonly _onCanvasDocumentFetchedSubject$ = new Subject<any>();
  public onCanvasDocumentFetched$: Observable<any> =
    this._onCanvasDocumentFetchedSubject$.asObservable();

  constructor(
    public readonly auth: AuthService,
    private readonly dialog: MatDialog,
    private readonly chatSocketService: ChatSocketService,
    private readonly haService: HealthAppService,
    private readonly snack: SnackbarService,
    private readonly apiHandler: CanvasApiHandlerService
  ) {
    this.currentUserEmail = this.auth.getUserEmail() ?? '';
  }

  /**
   * Initializes the CanvasService session with default values or provided currentSessionId.
   * @param currentSessionId - Optional Session ID to initialize the service with.
   */
  init(currentSessionId?: string) {
    this.currentSessionId = currentSessionId ?? this.getUniqueId();
    this.currentChatMessages = [];
    this.bookmarkedResponses = [];
    this.chatFilesList = [];
    this.bookmarkIndex = 0;
    this.setAgentWorking();
    this.isLoadingSessions = false;
    this.outlineDocument = null;
    this.chatWindowScreenSize = ChatExpansion.FULL_SCREEN_EXPANDED;
    this.showSessionsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.showCitationsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this._canvasEvents$.next({
      type: CanvasEvents.INIT,
      payload: { currentSessionId: this.currentSessionId },
    });
  }

  /**
   * Clears the current instance of the CanvasService, resetting all properties.
   */
  clearInstance() {
    this.currentSessionId = undefined;
    this.sessions = [];
    this.currentChatMessages = [];
    this.chatFilesList = [];
    this.bookmarkIndex = 0;
    this.bookmarkedResponses = [];
    this.setAgentWorking();
    this.isLoadingSessions = false;
    this.isEmbededChat = false;
    this.outlineDocument = null;
    this.chatWindowScreenSize = ChatExpansion.FULL_SCREEN_EXPANDED;
    this.showSessionsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.showCitationsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this._canvasEvents$.next({ type: CanvasEvents.CLEAR_INSTANCE });
  }

  /**
   * Creates a new chat session and initializes the service.
   */
  newChatSession() {
    this.init();
    this.createNewSession();
  }

  /**
   * Creates a new session and highlights it in the session list.
   */
  createNewSession() {
    this.sessions = this.sessions?.filter(
      (session: Session) => session.user_id?.length > 0
    );

    this.sessions?.forEach((session: Session) => {
      session.highlight = false;
    });

    const newSession: Session = {
      bookmarked: false,
      created_on: new Date().toISOString(),
      isSaving: false,
      displayDate: this.formatDateTime(new Date().toISOString()),
      is_active: true,
      isMySession: true,
      permission: ShareAccessLevel.MANAGE_SESSION,
      organization_id: this.auth.getOrgId(),
      title: 'New Chat',
      user_id: '',
      _id: this.currentSessionId,
      highlight: true,
      shared_with: [],
    };

    this.sessions.splice(0, 0, newSession);

    if (this.chatSocketService.isSocketCreated()) {
      this._canvasEvents$.next({
        type: CanvasEvents.NEW_SESSION_CREATED,
        payload: newSession,
      });
      return;
    }

    this.createSocketInstance();
  }

  createSocketInstance() {
    this.destroyNotifier$?.next();
    this.destroyNotifier$?.complete();
    this.destroyNotifier$ = new Subject<void>();

    this.haService.getChatSocketAccessUrl(SockeConnectionType.CHAT).subscribe({
      next: (response: any) => {
        if (response?.token_url) {
          this.chatSocketService.connect(response.token_url);
          this.chatSocketService.eventStream
            .pipe(takeUntil(this.destroyNotifier$))
            .subscribe({
              next: (event) => {
                this._canvasEvents$.next({
                  type: CanvasEvents.SOCKET_INIT,
                });
                console.log(`Canvas Socket Event [${event.type}]:`, event);
                this.handleSocketEvents(
                  {
                    type: event.type,
                    payload: event.data,
                  } as SocketEvent,
                  this.currentChatMessages?.find(
                    (x) => x._id === event.data.payload.conversationId
                  ),
                  event.data?.payload?.operation_type
                );
              },
              error: (error) => {
                console.error('WebSocket subscription error:', error);
              },
            });
        } else {
          console.error('No socket URL provided in response');
        }
      },
      error: (error) => {
        console.error('Error fetching socket access URL:', error);
      },
    });
  }

  /**
   * Displays an alert dialog to the user.
   * This method can be customized to show specific messages or actions.
   */
  showAlertDialog() {
    let dialogRef = this.dialog.open(AlertpopupComponent, {
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false,
      width: '50vw',
      backdropClass: 'backdropBackground',
      data: {
        title: 'Connection Lost',
        message: `We've lost connection to the server. Please check your network and try reconnecting to restore real-time updates.`,
        postitionButtonMessage: 'Reconnect Now',
        negativeButtonMessage: 'Close',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.restartAppNotificationSocket();
      }
    });
  }

  /**
   * Restarts the notification socket connection after a delay.
   * This is useful when the socket connection is lost or needs to be re-established.
   */
  restartAppNotificationSocket() {
    this.chatSocketService.forceClearConnection();
    setTimeout(() => {
      this.createSocketInstance();
    }, 2000);
  }

  closeOutline(clearSubs: boolean = true) {
    if (clearSubs) this.apiHandler.clearSubscriptions();

    this.chatWindowScreenSize = ChatExpansion.FULL_SCREEN_EXPANDED;
    this.isLoadingDocument = false;
    this.outlineDocument = null;
  }

  setAgentWorking(
    isAgentWorking?: boolean,
    conversation_id?: string,
    session_id?: string,
    isAgentRegenerating?: boolean
  ) {
    if (isAgentWorking == undefined) {
      this.agentTyping = {
        conversation_id: '',
        session_id: '',
        isAgentWorking: false,
        isAgentRegenerating: false,
      };
      return;
    }

    this.agentTyping = {
      isAgentWorking: isAgentWorking,
      conversation_id: isAgentWorking ? conversation_id : '',
      session_id: isAgentWorking ? session_id ?? this.currentSessionId : '',
      isAgentRegenerating: isAgentRegenerating ?? false,
    };
  }

  setChatItemInProgress() {
    let chatItem = this.currentChatMessages?.find(
      (x: any) =>
        x._id === this.agentTyping.conversation_id &&
        x.session_id === this.agentTyping.session_id
    );
    if (chatItem) {
      chatItem.status = ConverstationStatus.IN_PROGRESS;
    }
  }

  stopAgentWorking() {
    if (this.agentTyping.conversation_id && this.agentTyping.session_id) {
      this.apiHandler
        .stopGeneration(
          this.agentTyping.conversation_id,
          this.agentTyping.session_id
        )
        .subscribe();
    }
  }

  isAgentWorking(sessionId: string | null = null): boolean {
    if (sessionId != null) {
      return (
        this.agentTyping.isAgentWorking &&
        sessionId == this.agentTyping.session_id
      );
    }
    return this.agentTyping.isAgentWorking;
  }

  isAgentRegenerating(conversation_id: string, session_id: string): boolean {
    return (
      this.agentTyping.isAgentWorking &&
      this.agentTyping.isAgentRegenerating &&
      this.agentTyping.session_id === session_id &&
      this.agentTyping.conversation_id === conversation_id
    );
  }

  /**
   * Fetches the list of chat sessions from the server.
   * @param shouldFetchMessages - Whether to fetch messages for the first session.
   */
  getSessions(
    shouldFetchMessages: boolean = true,
    isNewSession: boolean = false
  ) {
    this.isLoadingSessions = true;
    this._canvasEvents$.next({ type: CanvasEvents.FETCH_SESSIONS });
    if (shouldFetchMessages) {
      this.apiHandler.clearSubscriptions();
    }
    this.apiHandler.pushSubscription(
      this.apiHandler.getSessionList().subscribe({
        next: (response: any) => {
          this.isLoadingSessions = false;
          let highlightedSession = cloneDeep(
            this.sessions.find((x: Session) => x.highlight)
          );
          this.processSessions(response);
          this._canvasEvents$.next({
            type: CanvasEvents.FETCH_SESSIONS_SUCCESS,
            payload: this.sessions,
          });

          if (
            this.sessions.length > 0 &&
            !isNewSession &&
            (highlightedSession?.user_id ?? '').length > 0
          ) {
            this.highlightSession(highlightedSession);
            if (shouldFetchMessages) {
              this.getChatMessageForSession(
                highlightedSession ?? this.sessions[0]
              );
            }
          } else {
            this.newChatSession();
          }
        },
        error: () => {
          this.isLoadingSessions = false;
          this._canvasEvents$.next({ type: CanvasEvents.FETCH_SESSIONS_ERROR });
        },
      })
    );
  }

  processSessions(response: any) {
    const sharedSessions = (response.shared_chat_sessions ?? []).map(
      (session: Session) => ({
        ...session,
        isMySession: false,
      })
    );
    const userSessions = (response.user_chat_sessions ?? []).map(
      (session: Session) => ({
        ...session,
        isMySession: true,
      })
    );

    let consolidatedSessions = [...sharedSessions, ...userSessions].sort(
      (a: Session, b: Session) =>
        new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
    );

    consolidatedSessions?.forEach((session: Session) => {
      session.displayDate = this.formatDateTime(session.created_on);
    });
    this.sessions = consolidatedSessions;
  }

  /**
   * Fetches chat messages for a specific session.
   * @param selectedSession - The session to fetch messages for.
   */
  getChatMessageForSession(selectedSession: any) {
    this.apiHandler.clearSubscriptions();
    this.currentSessionItem = cloneDeep(selectedSession);
    this.currentSessionId = selectedSession._id;
    this.highlightSession(selectedSession);
    this.currentChatMessages = [];
    this.chatFilesList = [];
    this.closeOutline(false);

    this._canvasEvents$.next({ type: CanvasEvents.FETCH_CHAT_MESSAGES });
    let documentChats: any = [];

    this.apiHandler.pushSubscription(
      this.getChatMessages(selectedSession).subscribe({
        next: (response: any) => {
          let conversations =
            response?.filter(
              (x: any) =>
                x.type === ChatTypes.CHAT || x.type == ChatTypes.CANVAS_CHAT
            ) ?? [];

          conversations.forEach((cov: any) => {
            cov.uploaded_files?.forEach((x: any) => {
              x.isUploading = false;
            });
            cov.responses?.forEach((x: any) => {
              x.response_text =
                x.response_text ?? x.response?.response_text ?? '';
              x.convertedMessage = this.getConvertedMessage(
                x.response_text ?? x.response?.response_text ?? ''
              );
              x.sources = x.sources ?? x.response?.sources ?? [];
              x.stats = x.stats ?? x.response?.stats;
              x.document = {
                document_id: x.document_id,
              };

              documentChats.push({ document_id: x.document_id, id: cov._id });

              delete x.document_id;
              delete x.response;
            });
          });

          this.currentChatMessages = conversations;
          this.formBookmarkedResponse();
          this.contentForkCalls(documentChats, conversations);
          this.checkIfAnyConversationInProgress();

          setTimeout(() => {
            this.triggerConversationNavigation(
              this.getLastConversationItem(),
              false
            );
          }, 100);

          this._canvasEvents$.next({
            type: CanvasEvents.FETCH_CHAT_MESSAGES_SUCCESS,
            payload: this.currentChatMessages,
          });
        },
        error: () => {
          this._canvasEvents$.next({
            type: CanvasEvents.FETCH_CHAT_MESSAGES_ERROR,
          });
        },
      })
    );
  }

  checkIfAnyConversationInProgress(coversation?: any): Promise<boolean> {
    return new Promise((resolve) => {
      let chatItem =
        coversation ??
        (this.currentChatMessages?.length > 0
          ? this.currentChatMessages[this.currentChatMessages.length - 1]
          : null);
      if (chatItem?.status === ConverstationStatus.IN_PROGRESS) {
        this.getConversationDetailsById(chatItem._id)
          .then((response: any) => {
            resolve(true);
          })
          .catch((error: any) => {
            resolve(true);
          });
      } else if (chatItem?.status === ConverstationStatus.COMPLETED) {
        if (
          this.agentTyping.isAgentWorking &&
          this.agentTyping.conversation_id === chatItem?._id
        ) {
          this.setAgentWorking();
        }
        resolve(true);
      } else {
        resolve(true);
      }
    });
  }

  getConversationDetailsById(_id: any): Promise<boolean> {
    return new Promise((resolve) => {
      if (!_id) {
        resolve(true);
      }
      this.apiHandler.getConversationById(_id).subscribe({
        next: (response: any) => {
          if (response.status == ConverstationStatus.COMPLETED) {
            if (
              this.agentTyping.isAgentWorking &&
              this.agentTyping.conversation_id === _id
            ) {
              this.setAgentWorking();
            }
            this.updateChatItem(response);
          } else if (response.status == ConverstationStatus.IN_PROGRESS) {
            this.setAgentWorking(true, response._id, response.session_id);
            this.setChatItemInProgress();
          }
          resolve(true);
        },
        error: () => {
          resolve(true);
        },
      });
    });
  }

  contentForkCalls(
    documentChats: [{ document_id: string; id: string }],
    conversations: any,
    showCanvas?: boolean
  ) {
    let convers = cloneDeep(conversations ?? []);
    const calls = documentChats.map((doc) => {
      if (doc?.document_id) {
        return this.apiHandler.getDocumentContent(doc.document_id).pipe(
          catchError((error) => {
            return of({
              error: true,
            });
          }),
          map((response: any) => {
            return {
              id: doc.id,
              document_id: doc.document_id,
              ...response,
            };
          })
        );
      } else {
        return of({
          id: null,
          document_id: null,
          error: true,
        });
      }
    });

    this.apiHandler.pushSubscription(
      forkJoin(calls).subscribe((results) => {
        results.forEach((callback: any, index: number) => {
          const chatMessage = convers.find(
            (chatItem: any) => chatItem._id === callback.id
          );
          if (chatMessage) {
            const responseItem = chatMessage.responses.find(
              (x: any) => x.document.document_id === callback.document_id
            );
            if (responseItem) {
              if (
                !callback.error &&
                callback.document_id &&
                callback?.versions?.length > 0
              ) {
                responseItem.document = {
                  type: callback.type,
                  versions: [callback.versions[callback.versions.length - 1]],
                  document_id: callback.document_id,
                };

                if (showCanvas) {
                  this.openCanvasForDocumentChatResponse({
                    document_id: callback.document_id,
                    version:
                      callback.versions[callback.versions.length - 1].version,
                    conversation_id: chatMessage?._id,
                  });
                }
              } else if (callback.error) {
                responseItem.document = {
                  type: null,
                  versions: [],
                  error: true,
                  document_id: callback.document_id,
                };
              }
            }
          }
        });
        if (convers?.length > 0) {
          this.currentChatMessages = convers;
        }
      })
    );
  }

  /**
   * Checks if any chat messages have bookmarks.
   * @returns True if there are bookmarked messages, false otherwise.
   */
  anyHavingBookmarks(): boolean {
    return (
      this.bookmarkedResponses?.length > 0 &&
      this.currentChatMessages.some((chatItem: any) => chatItem.bookmarked)
    );
  }

  /**
   * Forms a list of bookmarked responses from the current chat messages.
   */
  formBookmarkedResponse(): void {
    this.bookmarkIndex = 0;
    this.bookmarkedResponses = cloneDeep(
      this.currentChatMessages.filter((c: any) => c.bookmarked) ?? []
    );
  }

  /**
   * Decrements the bookmark index, wrapping around if necessary.
   */
  decrementBookmarkIndex(): void {
    if (this.bookmarkIndex == 0) {
      this.bookmarkIndex = this.bookmarkedResponses.length - 1;
      return;
    }
    this.bookmarkIndex -= 1;
  }

  /**
   * Increments the bookmark index, wrapping around if necessary.
   */
  incrementBookmarkIndex(): void {
    if (this.bookmarkIndex == this.bookmarkedResponses.length - 1) {
      this.bookmarkIndex = 0;
      return;
    }
    this.bookmarkIndex += 1;
  }

  /**
   * Triggers navigation to a specific conversation or bookmark.
   * @param conversation - The conversation to navigate to.
   * @param isBookmark - Whether the navigation is for a bookmark.
   */
  triggerConversationNavigation(
    conversation: any = null,
    isBookmark: boolean = true
  ): void {
    const payload = {
      conversation:
        conversation ?? this.bookmarkedResponses[this.bookmarkIndex],
      isBookmark: isBookmark,
    };
    this._canvasEvents$.next({
      type: CanvasEvents.CONVERSATION_NAVIGATION_TRIGGERED,
      payload,
    });
    this._onBookmarkNavigationSubject$.next(payload);
  }

  /**
   * Highlights a specific session in the session list.
   * @param selectedSession - The session to highlight.
   */
  highlightSession(selectedSession?: Session): void {
    this.sessions.forEach((session: Session, index: number) => {
      session.highlight = selectedSession
        ? session._id === selectedSession._id
        : index === 0;
    });
    this._canvasEvents$.next({
      type: CanvasEvents.SESSION_HIGHLIGHTED,
      payload:
        selectedSession ?? this.sessions?.find((x: Session) => x.highlight),
    });
  }

  /**
   * Gets a string representation of the bookmark counter.
   * @returns A string in the format "current/total".
   */
  getBookmarksCounterString(): string {
    return `${this.bookmarkedResponses.length}`; // ${this.bookmarkIndex + 1}/
  }

  /**
   * Generates a universally unique identifier (UUID).
   * @returns A UUID string.
   */
  getUniqueId(): string {
    return this.haService.getUniqueId();
  }

  /**
   * Generates a payload for the WebSocket connection based on the connection type and chat item.
   * @param connectionType - The type of socket connection (e.g., CHAT, CANVAS_CHAT).
   * @param chatItem - The chat item associated with the payload.
   * @param payload - Optional additional data for the payload.
   * @returns The generated SocketPayload object.
   */
  generatePayload(
    connectionType: string,
    chatItem: any,
    payload?: any
  ): SocketPayload {
    if (chatItem) {
      chatItem.isDocumentType = false;
    }
    let socketPayload: SocketPayload;
    switch (connectionType) {
      case SockeConnectionType.REGENERATE:
        socketPayload = {
          data: {
            conversationid: chatItem._id,
            sessionid: chatItem.session_id,
          },
          type: SockeConnectionType.REGENERATE,
        };
        return socketPayload;
      case SockeConnectionType.CANVAS_CHAT:
        socketPayload = {
          data: payload.payload,
          type: SockeConnectionType.CANVAS_CHAT,
        };
        return socketPayload;
      case SockeConnectionType.CHAT:
      default:
        socketPayload = {
          data: {
            utterance: payload.userText,
            sessionid: this.currentSessionId,
          },
          type: SockeConnectionType.CHAT,
        };
        if (payload.ofo) socketPayload.data.context = { nofo: payload.nofo };
        return socketPayload;
    }
  }

  /**
   * Handles socket events based on their type and connection type.
   * @param event - The socket event to handle.
   * @param chatItem - Optional chat item associated with the event.
   * @param connectionType - The type of socket connection (default: SockeConnectionType.CHAT).
   */
  handleSocketEvents(
    event: SocketEvent,
    chatItem?: any,
    connectionType: string = SockeConnectionType.CHAT
  ) {
    switch (event.type) {
      case SocketConnectionStage.MESSAGE:
        switch (connectionType) {
          case SockeConnectionType.REGENERATE:
            this.handleRegenerateSocketEventResponse(
              event.payload.payload,
              chatItem
            );
            break;
          case SockeConnectionType.CANVAS_CHAT:
            this.handleCanvasChatSocketEventResponse(
              event.payload.payload,
              chatItem
            );
            break;
          default:
            this.handleChatSocketEventResponse(event.payload.payload, chatItem);
            break;
        }
        break;
      case SocketConnectionStage.DISCONNECT:
        if (this.agentTyping.isAgentWorking) {
          this.getConversationDetailsById(this.agentTyping.conversation_id);
        } else {
          this.handleChatSocketErrorResponse(event);
        }
        break;
      case SocketConnectionStage.COMPLETE:
        this.setAgentWorking();
        break;

      default:
        console.info(`Unhandled socket stage: ${event.type}`);
        break;
    }
  }

  /**
   * Handles the response for a regenerate socket event.
   * @param payload - The socket message payload.
   * @param chatItem - Optional chat item associated with the event.
   */
  handleRegenerateSocketEventResponse(payload: SocketMessage, chatItem?: any) {
    switch (payload?.messageType) {
      case SocketResponseMessageType.CHAT_INITIALIZED:
        this.setAgentWorking(true, chatItem._id, payload.sessionId, true);
        this.setChatItemInProgress();
        break;

      case SocketResponseMessageType.AGENT_CHAT_DOCUMENT_RESPONSE:
        this.handleChatDocumentMessageType(payload, chatItem);
        break;

      case SocketResponseMessageType.AGENT_STATUS_UPDATE:
        this.setAgentStatusUpdate(payload);
        break;

      case SocketResponseMessageType.AGENT_RESPONSE:
        this.setAgentWorking();
        if (
          payload.sessionId == this.currentSessionId &&
          payload.conversationId == chatItem._id &&
          payload.response
        ) {
          chatItem.status = ConverstationStatus.COMPLETED;
          this.scrollDown(
            this.addChatMessages(
              payload.response,
              ChatMessageType.AGENT_MESSAGE,
              payload.conversationId
            )
          );
          this.snack.openSuccessBar('Response regenerated successfully');
          this.handleDocumentFailure(payload.response, chatItem);
        }
        break;

      default:
        console.info(`Unhandled message type: ${payload?.messageType}`);
        break;
    }
  }

  /**
   * Handles the response for a chat socket event.
   * @param payload - The socket message payload.
   * @param chatItem - Optional chat item associated with the event.
   */
  handleChatSocketEventResponse(payload: SocketMessage, chatItem?: any) {
    switch (payload?.messageType) {
      case SocketResponseMessageType.CHAT_INITIALIZED:
        this.handleChatInitialized(payload);
        break;
      case SocketResponseMessageType.AGENT_CHAT_DOCUMENT_RESPONSE:
        this.handleAgentChatDocumentResponse(payload, chatItem);
        break;
      case SocketResponseMessageType.AGENT_STATUS_UPDATE:
        this.setAgentStatusUpdate(payload);
        break;
      case SocketResponseMessageType.AGENT_RESPONSE:
        this.handleAgentResponse(payload, chatItem);
        break;
      case SocketResponseMessageType.AGENT_FAILED:
        this.handleAgentFailed(payload);
        break;
      default:
        console.info(`Unhandled message type: ${payload?.messageType}`);
        break;
    }
  }

  handleChatDocumentMessageType(payload: SocketMessage, chatItem: any) {
    this.setAgentStatusUpdate(payload);
    if (payload.sessionId == this.currentSessionId) {
      if (chatItem) {
        chatItem.isDocumentType = true;
        this.addTemporaryDocumentItem(chatItem);
      }
      this.showCanvas();
    }
  }

  addTemporaryDocumentItem(chatItem: any) {
    let item = {
      id: '',
      text: '',
      sources: [],
      stats: null,
      feedback: null,
      document: {
        document_id: CanvasStringConstants.TEMPORARY_DOCUMENT_ID,
      },
      convertedMessage: '',
      conversation_id: chatItem._id ?? chatItem.conversation_id ?? '',
      session_id: this.currentSessionId,
    };

    console.log('Adding temporary document item to chat responses', item);
    chatItem?.responses.push(item);
  }

  private handleChatInitialized(payload: SocketMessage) {
    let createdChatItem = this.addChatMessages(
      JSON.parse(payload.response as string)
    );
    this.setAgentWorking(true, createdChatItem._id, payload.sessionId);
    if (
      createdChatItem?.session_id == payload.sessionId &&
      !createdChatItem._id
    ) {
      createdChatItem._id = payload.conversationId;
    }
  }

  private handleAgentChatDocumentResponse(
    payload: SocketMessage,
    chatItem?: any
  ) {
    this.setAgentStatusUpdate(payload);
    if (payload.sessionId == this.currentSessionId) {
      let createdChatItem = chatItem;
      if (!chatItem) {
        createdChatItem = this.currentChatMessages?.find(
          (x) => x._id === payload.conversationId
        );
        if (createdChatItem) {
          createdChatItem.isDocumentType = true;
        }
      }
      if (chatItem) {
        chatItem.isDocumentType = true;
        this.addTemporaryDocumentItem(chatItem);
      }
      this.showCanvas();
    }
  }

  private handleAgentResponse(payload: SocketMessage, chatItem?: any) {
    this.setAgentWorking();
    if (payload.sessionId == this.currentSessionId) {
      this.scrollDown(
        this.addChatMessages(
          payload,
          ChatMessageType.AGENT_MESSAGE,
          payload.conversationId
        )
      );
      this.handleDocumentFailure(
        payload,
        chatItem ??
          this.currentChatMessages?.find(
            (x) => x._id === payload.conversationId
          )
      );
    }
  }

  private handleAgentFailed(payload: SocketMessage) {
    this.setAgentWorking();
    this.handleDocumentFailure(
      payload,
      this.currentChatMessages?.find((x) => x._id === payload.conversationId)
    );
  }

  /**
   * Handles the response for a canvas chat socket event.
   * @param payload - The socket message payload.
   * @param chatItem - Optional chat item associated with the event.
   */
  handleCanvasChatSocketEventResponse(payload: SocketMessage, chatItem?: any) {
    let createdChatItem!: any;
    switch (payload?.messageType) {
      case SocketResponseMessageType.CHAT_INITIALIZED:
        this.handleCanvasChatInitialized(payload);
        break;
      case SocketResponseMessageType.AGENT_CHAT_DOCUMENT_RESPONSE:
        this.handleCanvasAgentChatDocumentResponse(payload);
        break;
      case SocketResponseMessageType.AGENT_STATUS_UPDATE:
        this.setAgentStatusUpdate(payload);
        break;
      case SocketResponseMessageType.AGENT_RESPONSE:
        if (!chatItem) {
          createdChatItem = this.currentChatMessages?.find(
            (x) => x._id === payload.conversationId
          );
        }
        this.handleCanvasAgentResponse(payload, chatItem ?? createdChatItem);
        break;
      case SocketResponseMessageType.AGENT_FAILED:
        this.setAgentWorking();
        if (!chatItem) {
          createdChatItem = this.currentChatMessages?.find(
            (x) => x._id === payload.conversationId
          );
        }
        this.handleDocumentFailure(payload, chatItem ?? createdChatItem);
        break;
      default:
        console.info(`Unhandled message type: ${payload?.messageType}`);
        break;
    }
  }

  private handleCanvasChatInitialized(payload: SocketMessage) {
    let createdChatItem = this.addChatMessages(
      JSON.parse(payload.response as string)
    );
    this.setAgentWorking(true, createdChatItem._id, payload.sessionId);
    if (
      payload.sessionId === this.currentSessionId &&
      createdChatItem?.session_id == payload.sessionId &&
      !createdChatItem._id
    ) {
      createdChatItem._id = payload.conversationId;
    }
  }

  private handleCanvasAgentChatDocumentResponse(payload: SocketMessage) {
    this.setAgentStatusUpdate(payload);
    if (payload.sessionId == this.currentSessionId) {
      this.isLoadingDocument = true;
    }
  }

  private handleCanvasAgentResponse(payload: SocketMessage, chatItem?: any) {
    this.setAgentWorking();
    if (payload.sessionId == this.currentSessionId) {
      this.pushVersionToDocument(payload.response);
      if (payload.conversationId == chatItem._id) {
        chatItem.status = ConverstationStatus.COMPLETED;
        this.scrollDown(
          this.addChatMessages(
            payload.response,
            ChatMessageType.AGENT_MESSAGE,
            payload.conversationId
          )
        );
      }
    }
  }

  /**
   * Handles the failure of a document request.
   * @param payload - The socket message payload.
   * @param chatItem - Optional chat item associated with the event.
   */
  handleDocumentFailure(payload: any, chatItem: any) {
    let response = payload.response;
    if (
      chatItem?.isDocumentType &&
      response?.document == null &&
      response?.document_id == null
    ) {
      this.isLoadingDocument = false;
      this.outlineDocument = null;
    }
    if (chatItem) {
      chatItem.isDocumentType = false;
    }
  }

  /**
   * Handles socket error responses.
   * @param event - The socket event to handle.
   */
  handleChatSocketErrorResponse(event: SocketEvent) {
    if (
      event.payload?.reason == ConnectionReasons.FAILURE ||
      event.payload?.reason == ConnectionReasons.RETRIES_EXCEEDED ||
      event.payload?.reason == ConnectionReasons.ERROR
    ) {
      this.showErrorMessage(
        'Error occurred while processing your request, please try again'
      );
      this.showAlertDialog();
    } else if (event.payload?.reason == ConnectionReasons.MANUAL) {
      console.info(
        `Unhandled connection closed reason: ${event.payload?.reason}`
      );
    }
  }

  /**
   * Sets the agent status update based on the provided message.
   * @param message - The socket message to set the agent status from.
   */
  setAgentStatusUpdate(message?: SocketMessage) {
    if (message?.response) {
      if (!this.agentStatusUpdates) {
        this.agentStatusUpdates = new AgentStatusUpdate();
      }
      this.agentStatusUpdates.agentCurrentStatus =
        typeof message.response === 'string' ? message.response : '';
    } else {
      this.agentStatusUpdates = new AgentStatusUpdate();
    }
  }

  getInlineStatusUpdates(): string {
    return (
      this.agentStatusUpdates?.agentCurrentStatus ??
      CanvasStringConstants.INLINE_LOADER_TEXT
    );
  }

  getRobotStatusUpdates(): string {
    return (
      this.agentStatusUpdates?.agentCurrentStatus ??
      CanvasStringConstants.ROBOT_LOADER_TEXT
    );
  }

  /**
   * Posts a chat message via HTTP and handles the response.
   * @param userText - The message to send.
   * @param nofo - Optional context for the message.
   */
  postChatMessage(userText: string, nofo: string | undefined) {
    this.setAgentStatusUpdate();
    this.setAgentWorking(true);
    this.scrollDown();

    this.apiHandler
      .postChatMessage(
        this.generatePayload(SockeConnectionType.CHAT, null, {
          userText,
          nofo,
        }).data,
        this.currentSessionId
      )
      .subscribe({
        next: (response: any) => {
          if (!response) {
            this.snack.openErrorBar(
              'Error occurred while processing your request, please try again'
            );
            return;
          }

          this.addChatMessages({
            bookmarked: false,
            message: userText,
            responses: [],
            session_id: this.currentSessionId,
            type: ChatTypes.CHAT,
            status: ConverstationStatus.IN_PROGRESS,
            _id: response._id,
            uploaded_files: [],
          });

          this.setAgentWorking(true, response._id, this.currentSessionId);
          this.scrollDown();
        },
        error: (error) => {
          this.setAgentWorking();
          this.snack.openErrorBar(
            'Error occurred while processing your request, please try again'
          );
        },
      });
  }

  /**
   * Sends a canvas query via WebSocket.
   * @param payload - The payload for the canvas query.
   * @param document_id - The ID of the document.
   */
  sendCanvasQuery(
    payload: {
      type: ChatTypes;
      selected_text: string;
      utterance: string;
      version?: number;
    },
    document_id: any
  ) {
    this.setAgentStatusUpdate();
    this.setAgentWorking(true);
    this.scrollDown();

    this.apiHandler
      .sendCanvasQuery(
        this.generatePayload(SockeConnectionType.CANVAS_CHAT, null, {
          payload,
          document_id,
        }).data,
        document_id,
        this.currentSessionId
      )
      .subscribe({
        next: (response: any) => {
          if (!response) {
            this.snack.openErrorBar(
              'Error occurred while processing your request, please try again'
            );
            return;
          }

          this.addChatMessages(
            {
              bookmarked: false,
              message: payload.utterance,
              responses: [],
              session_id: this.currentSessionId,
              type: ChatTypes.CANVAS_CHAT,
              status: ConverstationStatus.IN_PROGRESS,
              _id: response.conversation_id,
              uploaded_files: [],
            },
            ChatMessageType.USER_MESSAGE,
            ChatTypes.CANVAS_CHAT
          );
          this.setAgentWorking(
            true,
            response.conversation_id,
            this.currentSessionId
          );
          this.scrollDown();
        },
        error: (error) => {
          console.error('Error fetching document version', error);
          this.setAgentWorking();
          this.snack.openErrorBar(
            'Error occurred while processing your request, please try again'
          );
        },
      });
  }

  /**
   * Regenerates a chat response for a specific chat item.
   * @param chatItem - The chat item to regenerate the response for.
   */
  regenerateChatResponse(chatItem: any) {
    if (this.isAgentRegenerating(chatItem._id, chatItem.session_id)) {
      return;
    }

    this.setAgentStatusUpdate();
    this.setAgentWorking(true, chatItem._id, chatItem.session_id, true);
    this.setChatItemInProgress();
    this.scrollDown();

    this.apiHandler
      .regenerateChatResponse(
        this.generatePayload(SockeConnectionType.REGENERATE, chatItem).data,
        this.currentSessionId
      )
      .subscribe({
        next: (response: any) => {
          if (!response) {
            this.snack.openErrorBar(
              'Error occurred while processing your request, please try again'
            );
            return;
          }
          this.scrollDown();
        },
        error: (error: any) => {
          this.setAgentWorking();
          this.snack.openErrorBar('Failed to regenerate response');
        },
      });
  }

  /**
   * Bookmarks or unbookmarks a response.
   * @param id - The ID of the response to bookmark or unbookmark.
   * @param action - The action to perform ("add" or "remove").
   */
  bookmarkResponse(chatItem: any, action: string = 'add') {
    chatItem.bookmarked = action === 'add';
    this.apiHandler
      .bookmarkChatItem(chatItem._id, action, this.currentSessionId)
      .subscribe((response: any) => {
        let session = this.sessions?.find(
          (session: Session) => session._id === this.currentSessionId
        );
        if (response.conversation_id && session) {
          session.bookmarked =
            action === 'add'
              ? true
              : this.currentChatMessages.some((chat: any) => chat.bookmarked);
        }

        this.formBookmarkedResponse();
        this._canvasEvents$.next({
          type:
            action === 'add'
              ? CanvasEvents.RESPONSE_BOOKMARKED
              : CanvasEvents.RESPONSE_UNBOOKMARKED,
          payload: { id: chatItem._id, action },
        });
      });
  }

  /**
   * Adds chat messages to the current session.
   * @param conversation - The conversation to add.
   * @param author - The author of the conversation ("user" or "agent").
   * @returns The added chat item.
   */
  addChatMessages(
    conversation: any,
    author: string = ChatMessageType.USER_MESSAGE,
    conversation_id?: string
  ): any {
    let chatItem: any = {};
    if (author === ChatMessageType.USER_MESSAGE) {
      chatItem = this.currentChatMessages.find(
        (x) => x._id === (conversation?.conversation_id ?? conversation?._id)
      );

      if (!chatItem) {
        chatItem = conversation;
        this.currentChatMessages.push(chatItem);
      }
    } else if (
      conversation.session_id === this.currentSessionId ||
      conversation.sessionId === this.currentSessionId
    ) {
      chatItem = conversation_id
        ? this.currentChatMessages.find(
            (x) => x.conversation_id === conversation_id
          ) ?? this.currentChatMessages[this.currentChatMessages.length - 1]
        : this.currentChatMessages[this.currentChatMessages.length - 1];
      chatItem._id =
        conversation.conversation_id ?? conversation.conversationId;
      chatItem.status = ConverstationStatus.COMPLETED;
      this.addResponseToChatItem(chatItem, conversation);
    }

    this.updateCurrentSession(
      chatItem,
      author === ChatMessageType.USER_MESSAGE
    );
    return chatItem;
  }

  /**
   * Adds a response to a specific chat item.
   * @param chatItem - The chat item to add the response to.
   * @param conversation - The response to add.
   */
  addResponseToChatItem(chatItem: any, conversation: any) {
    chatItem.responses.push({
      id: conversation.response?.id ?? conversation.id,
      text: conversation.response?.response_text,
      sources: conversation.response?.sources ?? [],
      feedback: conversation.response?.feedback ?? conversation.feedback,
      stats: conversation.response?.stats,
      document: {
        ...conversation.response?.document,
        document_id: conversation.response?.document_id,
      },
      convertedMessage: this.getConvertedMessage(
        conversation.response?.response_text ?? ''
      ),
      conversation_id:
        conversation.conversation_id ?? conversation.conversationId,
      session_id: conversation.session_id ?? conversation.sessionId,
    });

    setTimeout(() => {
      chatItem.responses = chatItem.responses.filter(
        (response: any) =>
          response.document.document_id !=
          CanvasStringConstants.TEMPORARY_DOCUMENT_ID
      );
    }, 100);

    if (conversation.response?.document_id) {
      if (conversation.response?.document?.versions?.length > 0) {
        this.openCanvasForDocumentChatResponse({
          document_id: conversation.response?.document_id,
          version: 1,
        });
      } else {
        this.contentForkCalls(
          [
            {
              document_id: conversation.response?.document_id,
              id: conversation.conversation_id,
            },
          ],
          this.currentChatMessages,
          true
        );
      }
    }
  }

  updateChatItem(conversation: any) {
    const index = this.currentChatMessages.findIndex(
      (x) => x._id === conversation._id
    );
    if (index !== -1) {
      this.currentChatMessages[index] = {
        ...conversation,
        uploaded_files: conversation.uploaded_files ?? [],
        responses: conversation.responses.map((currentResponse: any) => {
          return {
            ...currentResponse,
            id: currentResponse.id,
            text: currentResponse.response_text,
            sources: currentResponse.sources ?? [],
            feedback: currentResponse.feedback,
            stats: currentResponse.stats,
            document: {
              ...currentResponse.document,
              document_id: currentResponse.document_id,
            },
            convertedMessage: this.getConvertedMessage(
              currentResponse.response_text ?? ''
            ),
            conversation_id: conversation._id ?? conversation.conversation_id,
            session_id: conversation.session_id ?? conversation.sessionId,
          };
        }),
      };
    }

    if (
      this.agentTyping?.conversation_id == conversation._id &&
      this.agentTyping?.session_id == conversation.session_id
    ) {
      this.setAgentWorking();
    }
  }

  /**
   * Updates the current session with the latest chat item.
   * @param chatItem - The chat item to update.
   * @param isUserMessage - Whether the message is from the user.
   */
  openCanvasForDocumentChatResponse(
    conversation: any,
    delay: number = 500,
    isTemporaryDocument: boolean = false
  ) {
    if (isTemporaryDocument) {
      setTimeout(() => {
        this.showCanvas();
      }, delay);
      return;
    }
    this.clearDocumentTypeChats();
    this.setAgentStatusUpdate();
    if (conversation.document_id) {
      setTimeout(() => {
        this.showCanvas({
          version: conversation?.version,
          document_id: conversation.document_id,
          conversation_id: conversation.conversation_id,
        });
      }, delay);
    }
  }

  /**
   * Gets the name of the currently highlighted chat session.
   * @returns The name of the highlighted chat session.
   */
  getChatName() {
    return (
      this.sessions?.find((session: Session) => session.highlight)?.title ??
      'New Chat'
    );
  }

  /**
   * Converts a response message to a sanitized HTML format.
   * @param response - The response message to convert.
   * @returns The converted message as sanitized HTML.
   */
  getConvertedMessage(response: any): any {
    if (response) {
      return this.getSanitizedHtmlForChat(this.getConvertedMarkdown(response));
    }
    return '';
  }

  private sanitizeHtml(
    htmlString: string,
    options: {
      addCanvasImageClass?: boolean;
      addNewlyAddedHighlight?: boolean;
      addDeletedHighlight?: boolean;
    } = {}
  ): string {
    if (!htmlString) return '';

    let html = htmlString;
    html = html
      .replaceAll('<td>', '<td class="table-row-md-rich-td">')
      .replaceAll('<th>', '<th class="table-row-md-rich-th">');

    html = html.replaceAll('<img', '<img loading="lazy" ');

    html = html.replaceAll('<a', '<a target="_blank"');

    html = html
      .replaceAll('<h1', '<h1 class="rich-h1" ')
      .replaceAll('<h2', '<h2 class="rich-h2" ')
      .replaceAll('<h3', '<h3 class="rich-h3" ')
      .replaceAll('<h4', '<h4 class="rich-h4" ')
      .replaceAll('<h5', '<h5 class="rich-h5" ')
      .replaceAll('<h6', '<h6 class="rich-h6" ');

    if (options.addNewlyAddedHighlight || options.addDeletedHighlight) {
      html = html
        .replaceAll('&lt;&lt;&lt;', '<<<')
        .replaceAll('&gt;&gt;&gt;', '>>>');
      html = html.replaceAll('&lt;&lt;', '<<').replaceAll('&gt;&gt;', '>>');
    }

    if (options.addNewlyAddedHighlight) {
      html = html
        .replaceAll('[[[', '<span class="newly-added-content-highlight">')
        .replaceAll(']]]', '</span>');
    }
    if (options.addDeletedHighlight) {
      html = html
        .replaceAll('<<<', '<del class="deleted-content-highlight-canvas">')
        .replaceAll('>>>', '</del>');
    }

    if (!options || options.addCanvasImageClass) {
      html = html
        .replaceAll('[', '<span class="placeholder-added-content-highlight">[')
        .replaceAll(']', ']</span>');
    }

    return html;
  }

  getSanitizedHtmlForCanvas(htmlString: string): string {
    return this.sanitizeHtml(htmlString, {
      addCanvasImageClass: true,
    });
  }

  getSanitizedHtmlForChat(htmlString: string): string {
    return this.sanitizeHtml(htmlString);
  }

  getSanitizedHtmlForCanvasComparison(htmlString: string): string {
    return this.sanitizeHtml(htmlString, {
      addCanvasImageClass: true,
      addNewlyAddedHighlight: true,
      addDeletedHighlight: true,
    });
  }

  /**
   * Copies the content of a response to the clipboard.
   * @param contentDiv - The HTML element containing the response content.
   */
  async copyResponse(contentDiv: any) {
    if (!contentDiv) return;

    try {
      const htmlContent = contentDiv.nativeElement.innerHTML;
      const plainTextContent = contentDiv.nativeElement.innerText; // Extracts text without HTML tags

      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainTextContent], { type: 'text/plain' }),
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
      });

      await navigator.clipboard.write([clipboardItem]);

      this.snack.openSuccessBar('Copied!');
    } catch (err) {
      console.error('Error while copying content:', err);
      this.showErrorMessage('Failed to copy');
    }
  }

  getConversationSettings() {
    return this.apiHandler.getConversationSettings();
  }
  getAppSettings() {
    return this.apiHandler.getAppSettings();
  }
  /**
   * Deletes a chat session via an API call.
   * @param session - The session to delete.
   * @returns An observable for the HTTP response.
   */
  deleteSessionAPi(session: Session) {
    return this.apiHandler.deleteSessionAPi(session);
  }

  /**
   * Deletes a chat session based on the search term.
   * @param searchTerm - The search term to identify the session to delete.
   * @returns An observable for the HTTP response.
   */
  getUsersList(searchTerm: string) {
    return this.apiHandler.getUsersList(searchTerm);
  }

  /**
   * Gets chat messages for a specific session.
   * @param session - The session to fetch messages for.
   * @returns An observable for the HTTP response.
   */
  getChatMessages(session: Session) {
    return this.apiHandler.getConversationMessages(session);
  }

  /**
   * Changes the title of a chat session.
   * @param id - The ID of the session to change the title for.
   * @param title - The new title for the session.
   * @returns An observable for the HTTP response.
   */
  changeChatTitle(session_id: string, title: string) {
    return this.apiHandler.changeSessionTitle(session_id, title);
  }

  /**
   * Sends a comment or feedback via an API call.
   * @param payload - The payload containing the comment details.
   * @returns An observable for the HTTP response.
   */
  sendComment(payload: any) {
    return this.apiHandler.sendFeedbackComment(payload, this.currentSessionId);
  }

  /**
   * Exports a document by its ID.
   * @param documentid - The ID of the document to export.
   * @returns An observable for the HTTP response.
   */
  exportDocument(documentid: any) {
    return this.apiHandler.downloadDocument(documentid);
  }

  /**
   * Uploads additional documents to the server.
   * @param fileLookups - The list of files to upload.
   * @returns An observable for the HTTP response.
   */
  uploadOtherDocuments(fileLookups: AttachedChatFile[]) {
    return this.apiHandler.uploadDocuments(fileLookups, this.currentSessionId);
  }

  /**
   * Updates the shared list of users.
   * @param sharedList - The list of users to share with.
   * @returns An observable for the HTTP response.
   */
  updateSharedList(sharedList: SharedUser[], sessionId: string) {
    return this.apiHandler.updateSharedList(
      {
        users: [...sharedList],
      },
      sessionId ?? this.currentSessionId
    );
  }

  /**
   * Uploads documents and handles the response.
   * @param meta - The metadata for the documents to upload.
   */
  uploadDocs(meta: any, isRetry: boolean = false) {
    this.setAgentStatusUpdate({
      response: `Please wait, uploading file${meta?.length > 1 ? 's' : ''}...`,
    });
    this.setAgentWorking(true);
    let chatItem: any;
    if (!isRetry) {
      chatItem = this.addChatMessages(
        {
          bookmarked: false,
          message: '',
          responses: [],
          session_id: this.currentSessionId,
          type: ChatTypes.CHAT,
          _id: '',
          status: ConverstationStatus.IN_PROGRESS,
          uploaded_files: meta?.map((file: any) => ({
            description: file.description,
            name: file.file_name,
            isUploading: true,
          })),
        },
        ChatMessageType.USER_MESSAGE
      );

      setTimeout(() => {
        this.triggerConversationNavigation(null, false);
      }, 100);
    }

    this.uploadOtherDocuments(meta).subscribe({
      next: (response: any) => {
        if (response?.conversation_id && response.status === 'success') {
          this.handleSuccessfulUpload(response, chatItem);
        } else {
          this.handleUploadError(chatItem);
        }
        this.setAgentWorking();
      },
      error: () => {
        this.handleUploadError(chatItem);
        this.setAgentWorking();
      },
    });
  }

  /**
   * Handles a successful document upload.
   * @param response - The response from the upload API.
   * @param chatItem - The chat item associated with the upload.
   */
  private handleSuccessfulUpload(response: any, chatItem: any): void {
    chatItem._id = response.conversation_id;

    this.addChatMessages(
      response,
      ChatMessageType.AGENT_MESSAGE,
      response.conversation_id
    );

    setTimeout(() => {
      this.triggerConversationNavigation(chatItem, false);
    }, 100);

    chatItem.uploaded_files.forEach((file: any) => {
      file.isUploading = false;
    });
  }

  /**
   * Handles an upload error.
   * @param chatItem - The chat item associated with the upload.
   */
  private handleUploadError(chatItem: any): void {
    this.showErrorMessage(
      'Error occurred while uploading the file, please upload again'
    );

    chatItem.status = 'error';
    chatItem.uploaded_files.forEach((file: any) => {
      file.isUploading = false;
    });
  }

  /**
   * Deletes a session from the local session list.
   * @param session - The session to delete.
   */
  deleteSessionFromList(session: Session) {
    let index = this.sessions.findIndex((x: Session) => x._id === session?._id);
    if (index != -1) {
      this.sessions.splice(index, 1);
    }
  }

  deleteSessionConfirmation(session: Session) {
    this.confirmDelete()
      .then((response: any) => {
        this.deleteSession(session);
      })
      .catch((error: any) => {});
  }

  confirmDelete() {
    return new Promise((resolve, reject) => {
      let dialogRef = this.dialog.open(AlertpopupComponent, {
        hasBackdrop: true,
        disableClose: true,
        autoFocus: false,
        width: '50vw',
        backdropClass: 'backdropBackground',
        data: {
          title: `Delete Session`,
          message: `Are you sure you want to delete this session? This cannot be undone.`,
          deleteButtonMessage: 'Yes, proceed',
          negativeButtonMessage: 'Cancel',
        },
      });
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result) {
          resolve(true);
          return;
        }
        reject(new Error('User cancelled the operation.'));
      });
    });
  }

  /**
   * Deletes a chat session and updates the UI.
   * @param session - The session to delete.
   */
  deleteSession(session: Session) {
    this._canvasEvents$.next({ type: CanvasEvents.DELETE_SESSION });
    this.isLoadingSessions = true;
    this.deleteSessionFromList(session);
    this.deleteSessionAPi(session).subscribe({
      next: (response: any) => {
        this.isLoadingSessions = false;
        if (
          !this.sessions.length ||
          (this.sessions.length == 1 && this.sessions[0].user_id == '')
        ) {
          this.newChatSession();
          this._canvasEvents$.next({
            type: CanvasEvents.FETCH_SESSIONS_SUCCESS,
          });
          return;
        }

        if (session.highlight) {
          this._canvasEvents$.next({
            type: CanvasEvents.DELETE_SESSION_SUCCESS,
          });
        } else {
          this._canvasEvents$.next({
            type: CanvasEvents.DELETE_SESSION_SUCCESS_WITHOUT_HIGHLIGHT,
          });
        }
      },
      error: (error: any) => {
        this.isLoadingSessions = false;
        this._canvasEvents$.next({ type: CanvasEvents.DELETE_SESSION_ERROR });
      },
    });
  }

  /**
   * Opens the feedback dialog for a specific chat.
   * @param chat - The chat to provide feedback for.
   */
  openKnowledgeChatCommentDialog(
    chat: any,
    currentChatResponseItem: any,
    isPositiveFeedback: boolean = true
  ) {
    this.dialog.open(CanvasChatFeedbackDialogComponent, {
      width: '65vw',
      disableClose: false,
      hasBackdrop: true,
      data: { chatDetails: chat, currentChatResponseItem, isPositiveFeedback },
    });
  }

  /**
   * Opens the add documents dialog for a chat.
   * @param fileSelected - The files selected for upload.
   */
  openAddChatDocumentsDialog(fileSelected: any) {
    let dialogRef = this.dialog.open(AddChatDocumentsDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false,
      width: '70vw',
      backdropClass: 'backdropBackground',
      data: {
        filesSelected: fileSelected,
        sectionPreselected: [],
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.uploading && result?.meta) {
        this.uploadDocs(result.meta);
      }
    });
  }

  /**
   * Opens an external link in a new tab.
   * @param url - The URL to open.
   */
  openExternalLink(url: any) {
    this.haService.openExternalWebPage(url, this.haService.getShortUrl(url));
  }

  /**
   * Gets the details of a specific document.
   * @param document - The document to fetch details for.
   * @returns An observable for the HTTP response.
   */
  getDocumentDetails(document: any) {
    return this.apiHandler.getDocumentDetails(document);
  }

  /**
   * Gets the details of a local Excel dataset.
   * @param excel_dataset_id - The ID of the dataset to fetch details for.
   * @returns An observable for the HTTP response.
   */
  getLocalExcelDetails(excel_dataset_id: any) {
    return this.apiHandler.getLocalExcelDetails(excel_dataset_id);
  }

  /**
   * Gets the last conversation item from the current chat messages.
   * @returns The last conversation item, or null if none exist.
   */
  getLastConversationItem(): any {
    return (
      findLast(
        this.currentChatMessages,
        (chatItem: any) => chatItem.responses?.length > 0
      ) ?? null
    );
  }

  /**
   * Formats a datetime string into a human-readable format.
   * @param datetime - The datetime string to format.
   * @returns A formatted string (e.g., "Today", "Yesterday", or "DD MMM, YYYY").
   */
  formatDateTime(datetime: string): string {
    if (!datetime) {
      return 'Today';
    }
    const itemDate = new Date(datetime);
    const today = new Date();

    // Reset the time component to 00:00:00 for both dates
    itemDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - itemDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return 'Previous 7 Days';
    } else if (diffDays <= 30) {
      return 'Previous 30 Days';
    } else {
      const options = { year: 'numeric', month: 'long' } as const;
      return itemDate.toLocaleDateString(undefined, options);
    }
  }

  /**
   * Toggles the visibility of the drawer panel.
   */
  toggleDrawer() {
    this.showSessionsPanel = !this.showSessionsPanel;
    this.clearCitations();
  }

  /**
   * Closes the drawer panel and clears citations.
   */
  closeDrawer() {
    this.showSessionsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.showCitationsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.clearCitations();
  }

  triggerClearCanvasEvent() {
    this._onCanvasDocumentFetchedSubject$.next({
      type: CanvasEvents.CANVAS_DOCUMENT_FETCHED_ERROR,
    });
  }

  clearDocumentTypeChats() {
    this.currentChatMessages?.forEach((chatItem: any) => {
      chatItem.isDocumentType = false;
    });
  }

  /**
   * Shows the canvas for a specific document.
   * @param docDetails - The details of the document to show.
   * @param chatWindowScreenSize - The screen size for the chat window.
   */
  showCanvas(
    docDetails: {
      version: number;
      document_id: string;
      conversation_id: string;
    } | null = null,
    chatWindowScreenSize: boolean = ChatExpansion.SMALL_SCREEN_LEFT
  ) {
    this.closeDrawer();
    this.chatWindowScreenSize = chatWindowScreenSize;
    this.outlineDocument = null;
    this.isLoadingDocument = true;

    if (docDetails)
      this.apiHandler.pushSubscription(
        this.apiHandler.getDocumentContent(docDetails.document_id).subscribe({
          next: (response: any) => {
            if (response?.versions?.length > 0) {
              this.outlineDocument = {
                ...response,
                conversation_id: docDetails.conversation_id,
              };
              this._onCanvasDocumentFetchedSubject$.next({
                type: CanvasEvents.CANVAS_DOCUMENT_FETCHED,
              });
            } else {
              this.showErrorMessage(
                'Failed to load outline. Please try again later.'
              );
              this.triggerClearCanvasEvent();
            }
          },
          error: (error) => {
            this.showErrorMessage(
              'Failed to load outline. Please try again later.'
            );
            this.triggerClearCanvasEvent();
          },
        })
      );
  }

  /**
   * Converts document content to HTML format.
   * @param content - The content to convert.
   * @param isLastVersion - Whether this is the last version of the document.
   * @returns A promise that resolves to the converted HTML string.
   */
  convertDocuemntContentToHtml(content: any, isLastVersion: boolean): string {
    let convertedContent = '';
    try {
      convertedContent = this.getSanitizedHtmlForCanvas(
        isLastVersion && content?.type != ChatTypes.SCORECARD
          ? this.processHeaderTagGrouping(
              this.getRichTextContentForCanvas(content, isLastVersion)
            )
          : this.getRichTextContentForCanvas(content, isLastVersion)
      );
    } catch (error) {
      console.error('Error in converting document content:', error);
      this.showErrorMessage('Failed to load outline. Please try again later.');
    }
    return convertedContent;
  }

  private getRichTextContentForCanvas(
    response: any,
    isLastVersion: boolean = false
  ): string {
    const rendererDocument = new Renderer();

    const findLastToken = (tokens: any[]): any => {
      if (!tokens || tokens.length === 0) return null;

      const lastToken = tokens[tokens.length - 1];
      if (lastToken.tokens && lastToken.tokens.length > 0) {
        return findLastToken(lastToken.tokens); // Recursively check the child tokens
      }
      return lastToken; // Return the last token when no more child tokens exist
    };

    const parseTokens = (tokens: any[]): string => {
      return tokens
        .map((token: any) => {
          if (
            token.type === 'em' ||
            token.type === 'strong' ||
            token.type === 'del' ||
            token.type === 'underline'
          ) {
            const innerContent = parseTokens(token.tokens ?? []);
            if (token.type === 'em') {
              return `<em>${innerContent}</em>`;
            } else if (token.type === 'strong') {
              return `<strong>${innerContent}</strong>`;
            } else if (token.type === 'del') {
              return `<s>${innerContent}</s>`;
            } else if (token.type === 'underline') {
              return `<u>${innerContent}</u>`;
            }
          }
          return token.raw ?? token.text ?? '';
        })
        .join('');
    };

    let h3Count = 0;
    rendererDocument.heading = ({
      tokens,
      depth,
    }: {
      tokens: any;
      depth: number;
    }): string => {
      const text = findLastToken(tokens)?.text ?? '';
      const convertedText = parseTokens(tokens);
      if (depth === 3) {
        h3Count++;
        return `
            <h${depth} id="${h3Count}" tag="${text}">
              ${convertedText}
              <span class="icon-wrapper-markdown" data-action="heading-icon"
                tag="${text}" id="${h3Count}" contenteditable="false">
              </span>
            </h${depth}>
          `;
      }
      return `<h${depth}>${convertedText}</h${depth}>`;
    };

    let content =
      typeof response === 'string' ? response : response.content ?? '';
    return this.getConvertedMarkdown(
      content,
      response.type != ChatTypes.SCORECARD && isLastVersion
        ? rendererDocument
        : undefined
    );
  }

  getConvertedMarkdown(response: any, renderer?: Renderer | undefined): string {
    return renderer
      ? marked(response ?? '', {
          ...marked.getDefaults(),
          renderer: renderer,
          async: false,
          breaks: true,
        })
      : marked(response ?? '', {
          ...marked.getDefaults(),
          async: false,
          breaks: true,
        });
  }

  scrollDown(chatItem: any = null, isBookmark: boolean = false) {
    if (!this.currentChatMessages?.length) {
      return;
    }
    try {
      if (isBookmark) {
        let nativeElement: any = document?.getElementById(
          `chat-${chatItem?._id ?? ''}`
        );
        nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
        nativeElement.classList.add('highlight');
        setTimeout(() => nativeElement.classList.remove('highlight'), 1500);
      } else {
        setTimeout(
          () => {
            let nativeElement: any = document?.getElementById(
              chatItem ? `chat-${chatItem._id}` : 'chat-loader-ui'
            );
            nativeElement ??= document?.getElementById('chat-');
            nativeElement?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start',
            });
          },
          chatItem ? 300 : 10
        );
      }
    } catch (error) {
      console.error('Error scrolling down:', error);
    }
  }

  processHeaderTagGrouping(htmlContent: string): string {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    const h3Elements = div.querySelectorAll('h3');

    if (h3Elements.length === 0) {
      return htmlContent;
    }
    const groupedContent: string[] = [];

    if (!htmlContent.startsWith('<h3')) {
      let startingContent = htmlContent.substring(
        0,
        htmlContent.indexOf('<h3')
      );
      groupedContent.push(startingContent);
    }

    let count = 1;
    h3Elements.forEach((h3, index) => {
      const nextH3 = h3Elements[index + 1];
      const sectionContent = [];

      let currentNode = h3.nextElementSibling;
      while (currentNode && currentNode !== nextH3) {
        sectionContent.push(currentNode.outerHTML || currentNode.textContent);
        currentNode = currentNode.nextElementSibling;
      }

      groupedContent.push(
        `<div class="h3-section-group" data-block="${count}">${
          h3.outerHTML
        }${sectionContent.join('')}</div>`
      );

      count++;
    });

    return groupedContent.join('');
  }

  updateCurrentSession(chatItme: any, shouldUpdateTitleOnly?: boolean) {
    let session = this.sessions.find(
      (x: Session) => x._id === chatItme?.session_id
    );
    if (session && !session.user_id?.length) {
      if (!shouldUpdateTitleOnly) {
        session.user_id = this.auth.getUserId();
      }
      if (chatItme.message) {
        session.title = chatItme.message.uploaded_files
          ? 'Uploaded files'
          : chatItme.message;
      }
    }
  }

  getMarkedContent(content: any): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        try {
          const gfm = require('turndown-plugin-gfm')?.gfm;
          if (gfm) {
            this.turndownService.use(gfm);
          }
        } catch (error) {
          console.error('Error loading turndown plugin:', error);
        }
        let markedContent = this.turndownService.turndown(content);
        if (markedContent) {
          markedContent = markedContent.replaceAll(
            '![icon](assets/icons/svg/edit.svg)',
            ''
          );
        }

        resolve(markedContent);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  showErrorMessage(message: string) {
    this.snack.openErrorBar(message);
  }

  openCitationsDialog(sourcesList: any[]) {
    this.sourcesList = sourcesList;
    this.showSessionsPanel = ChatExpansion.SMALL_SCREEN_LEFT;
    this.showCitationsPanel = ChatExpansion.FULL_SCREEN_EXPANDED;
  }

  getMarkdownContent(htmlContent: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.getMarkedContent(htmlContent)
        .then((mdContent) => {
          if (mdContent) {
            resolve(mdContent);
          } else {
            reject(new Error('Failed to convert HTML to Markdown'));
          }
        })
        .catch((err) => {
          console.error('Error fetching marked content:', err);
          reject(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {});
    });
  }

  saveDocument(
    payload: { title: any; content: string },
    document_id: string
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.apiHandler
        .saveDocumentVersion(payload, document_id, this.currentSessionId)
        .subscribe({
          next: (response: any) => {
            resolve(true);
            this.pushVersionToDocument(response);
          },
          error: () => {
            console.error('Error fetching document version');
            this.showErrorMessage(
              'Failed to save document version. Please try again later.'
            );
            resolve(false);
          },
        });
    });
  }

  pushVersionToDocument(response: any) {
    if (
      response.document_version?.versions?.length > 0 &&
      response.session_id == this.currentSessionId &&
      this.outlineDocument?._id == response.document_version?.document_id &&
      this.outlineDocument?.conversation_id ==
        response.document_version?.conversation_id
    ) {
      this.outlineDocument.versions.push(...response.document_version.versions);
      this._onCanvasDocumentFetchedSubject$.next({
        type: CanvasEvents.CANVAS_DOCUMENT_FETCHED,
      });
      this.snack.openSuccessBar('Document updated successfully');
    } else if (
      !response.document_version?.versions?.length &&
      response.session_id == this.currentSessionId
    ) {
      this._onCanvasDocumentFetchedSubject$.next({
        type: CanvasEvents.CANVAS_DOCUMENT_FETCHED_ERROR,
      });
      this.showErrorMessage('Failed to update document');
    }
  }

  onKeyDown(event: KeyboardEvent) {}

  /**
   * Cleans up resources when the service is destroyed.
   */
  ngOnDestroy(): void {
    this.destroyNotifier$?.next();
    this.destroyNotifier$?.complete();
    this.chatSocketService.forceClearConnection();
    this.apiHandler.clearSubscriptions();
  }
}
